import { useEffect, useState } from 'react';
import { Link, Outlet, useNavigate, useSearch } from '@tanstack/react-router';
import { ArrowLeft } from 'lucide-react';
import {
  parseActionParam,
  parseReturnToParam,
} from '@/lib/finance/searchParamNamespace';
import { useContributionDetail } from '@/hooks/queries/useFinanceContributions';
import { cn } from '@/lib/utils';
import { RefundDialog } from './RefundDialog';
import { ApiFailure } from '@signalvote/shared';

/**
 * CPH-011 parent shell for `/admin/finance/close/*`. The reason this isn't
 * the existing `CloseLayout` (which only mounts at the index route) is the
 * AC clause "the affordance persists across all Close sub-routes" — only a
 * parent-route wrapper can do that without copy-paste in every leaf.
 *
 * `?action=rapid` is deliberately left alone here. `ActionQueue` reads the
 * same param to pin its queue open (auto-expanded) when a rapid item is
 * present, so the deep-linked item is visible; clearing it from this layer
 * would defeat that on the very next render.
 */
export function CloseHubShell() {
  return (
    <>
      <BackToReportHeader />
      <ActionDispatcher />
      <Outlet />
    </>
  );
}

function BackToReportHeader() {
  const search = useSearch({ strict: false }) as {
    returnTo?: unknown;
    reportId?: unknown;
  };
  const returnTo = parseReturnToParam(search.returnTo);
  const reportId =
    typeof search.reportId === 'string' ? search.reportId : null;

  if (returnTo !== 'report' || !reportId) return null;

  return (
    <div
      data-slot="close-back-to-report"
      className="mb-3 flex items-center"
    >
      <Link
        to="/admin/finance/reports/$reportId"
        params={{ reportId }}
        data-testid="close-back-to-report"
        className={cn(
          'inline-flex items-center gap-1.5 rounded-md border border-border',
          'bg-muted px-2.5 py-1 text-xs font-medium text-muted-foreground',
          'hover:bg-accent hover:text-accent-foreground',
        )}
      >
        <ArrowLeft className="h-3.5 w-3.5" />
        Back to report
      </Link>
    </div>
  );
}

function ActionDispatcher() {
  const search = useSearch({ strict: false }) as {
    action?: unknown;
    contributionId?: unknown;
    excess?: unknown;
  };
  const action = parseActionParam(search.action);
  const contributionId =
    typeof search.contributionId === 'string' && search.contributionId.length > 0
      ? search.contributionId
      : null;
  // Only a positive number is a usable pre-fill; a mangled link degrades to an empty amount
  // field rather than proposing nonsense. (The route's validateSearch already applies this
  // rule; repeated here because this read is deliberately `strict: false`.)
  const excess =
    typeof search.excess === 'number' && search.excess > 0 ? search.excess : undefined;

  // The dialog's mount decision is owned by component state, NOT the live URL.
  // The deep link seeds this latch once; after that the dispatcher clears the
  // URL params (so a refresh doesn't reopen the dialog) WITHOUT the clear
  // unmounting the still-open dialog. If the mount gated on `search.action`
  // directly, the params-clearing effect would immediately unmount it and the
  // deep link would only flash. State is `null` until the deep-link fires and
  // stays put through the clear until the user closes.
  const [dispatched, setDispatched] = useState<string | null>(null);
  // The proposed amount latches WITH the contribution: the params-clearing effect wipes
  // `?excess` from the URL a tick after the dialog mounts, so reading it live would blank the
  // pre-filled field the moment the treasurer looked at it.
  const [dispatchedExcess, setDispatchedExcess] = useState<number | undefined>(undefined);

  useEffect(() => {
    if (action === 'refund' && contributionId) {
      // Latch the contribution once. Re-running with a new id (a fresh deep
      // link while one is already open) re-seeds it; the same id is idempotent.
      setDispatched((prev) => {
        if (prev === contributionId) return prev;
        setDispatchedExcess(excess);
        return contributionId;
      });
    }
  }, [action, contributionId, excess]);

  if (!dispatched) return null;

  return (
    <RefundDispatch
      contributionId={dispatched}
      excessAmount={dispatchedExcess}
      onClose={() => setDispatched(null)}
    />
  );
}

interface RefundDispatchProps {
  contributionId: string;
  /**
   * GitHub #61 — the amount the cure should propose, computed and clamped SERVER-side on the
   * preflight warning whose Resolve affordance produced this link. Undefined when the dialog
   * was reached some other way (the ledger's row action, a hand-typed URL), in which case the
   * treasurer enters the amount themselves.
   */
  excessAmount?: number;
  onClose: () => void;
}

function RefundDispatch({ contributionId, excessAmount, onClose }: RefundDispatchProps) {
  const navigate = useNavigate();
  const read = useContributionDetail(contributionId);
  const isLoading = read.status === 'loading' || read.status === 'idle';
  // Local open state so the close animation runs before the parent tears the
  // mount down. The parent latch (`dispatched`) owns whether we exist at all;
  // this owns the open/closed transition and calls back on dismiss.
  const [open, setOpen] = useState(true);

  useEffect(() => {
    if (isLoading) return;
    // Object-rest destructure is structurally narrower than "delete known
    // keys" — any param a future ticket adds to the contract survives by
    // default without dispatcher-side knowledge. Scope is strict per AC:
    // `?action` + `?contributionId` only. Clearing these here is now SAFE for
    // the dialog: the parent's `dispatched` latch keeps the mount alive, so
    // the clear no longer auto-closes the deep-linked dialog.
    void navigate({
      replace: true,
      // eslint-disable-next-line @typescript-eslint/no-explicit-any
      search: ((prev: Record<string, unknown>) => {
        const { action: _action, contributionId: _contributionId, ...rest } =
          prev ?? {};
        return rest;
      }) as any,
    });
  }, [isLoading, navigate]);

  const handleOpenChange = (next: boolean) => {
    setOpen(next);
    if (!next) onClose();
  };

  if (isLoading) return null;

  if (read.status !== 'ready') {
    // Only a 404 means the row is genuinely gone. Anything else — a 500, a dropped
    // connection — is our failure, and saying "couldn't be found" sends the treasurer
    // hunting for a record that is sitting right there in their ledger.
    const isMissing = read.status === 'failed' && read.error instanceof ApiFailure && read.error.status === 404;
    return (
      <div
        role="alert"
        data-testid="close-refund-dispatch-error"
        className="mb-3 rounded-md border border-destructive bg-destructive/5 px-3 py-2 text-sm text-destructive"
      >
        {isMissing
          ? "That contribution couldn't be found."
          : 'Something went wrong loading that contribution. Nothing has been changed — refresh the page to try again.'}
      </div>
    );
  }

  return (
    <RefundDialog
      contribution={read.data}
      excessAmount={excessAmount}
      open={open}
      onOpenChange={handleOpenChange}
    />
  );
}
